import { writable, get } from 'svelte/store';
import { MediaPipeTeleopDevice as MediaPipeTeleopDeviceBase } from '$lib/services/mediapipe-teleop-device';
import type { ControllerState as ControllerStateBase } from '$lib/services/mediapipe-teleop-device';
import { MediaPipeTeleopDevice as MediaPipeTeleopDeviceWithHands } from '$lib/services/mediapipe-teleop-device-with-hands';
import type { ControllerState as ControllerStateWithHands } from '$lib/services/mediapipe-teleop-device-with-hands';
import type { vec3 } from 'gl-matrix';

export type MediaPipeTeleopDevice = MediaPipeTeleopDeviceBase | MediaPipeTeleopDeviceWithHands;
export type ControllerState = ControllerStateBase | ControllerStateWithHands;

/**
 * Store for MediaPipe body tracking and teleoperation
 */

// MediaPipe teleoperation device instance
export const teleopDevice = writable<MediaPipeTeleopDevice | null>(null);

// Current controller state (SEW poses, wrist poses, hand poses, engagement status)
export const controllerState = writable<ControllerState | null>(null);

// Tracking status
export const isTracking = writable<boolean>(false);
export const isEngaged = writable<boolean>(false);

/**
 * Initialize the MediaPipe teleoperation device
 */
export function initializeTeleopDevice(options: {
  debug?: boolean;
  mirrorActions?: boolean;
  useHands?: boolean;
} = {}): MediaPipeTeleopDevice {
  // Close existing device if any
  const existingDevice = get(teleopDevice);
  if (existingDevice) {
    existingDevice.close();
  }

  const useHands = options.useHands ?? true;
  const DeviceClass = useHands ? MediaPipeTeleopDeviceWithHands : MediaPipeTeleopDeviceBase;
  const device = new DeviceClass({
    debug: options.debug ?? false,
    mirrorActions: options.mirrorActions ?? false
  });

  teleopDevice.set(device);
  return device;
}

/**
 * Start teleoperation tracking
 */
export function startTracking(): void {
  isTracking.set(true);
  
  // Get the device and start control if available
  teleopDevice.subscribe(device => {
    if (device) {
      device.startControl();
    }
  })();
}

/**
 * Stop teleoperation tracking
 */
export function stopTracking(): void {
  isTracking.set(false);
  isEngaged.set(false);
  controllerState.set(null);
}

/**
 * Update controller state from the teleoperation device
 * Call this in your render loop
 */
export function updateControllerState(device: MediaPipeTeleopDevice): void {
  const state = device.getControllerState();
  controllerState.set(state);
  isEngaged.set(state.engaged);
}

/**
 * Get SEW and wrist poses from the current controller state
 */
export function getSewAndWristPoses() {
  const currentState = get(controllerState);

  if (!currentState) {
    return {
      sew_poses: {
        left: { S: null, E: null, W: null },
        right: { S: null, E: null, W: null },
        R_world_body: null
      },
      wrist_poses: {
        left: null,
        right: null
      },
      torso_angles: null
    };
  }

  return {
    sew_poses: currentState.sew_poses,
    wrist_poses: currentState.wrist_poses,
    torso_angles: 'torso_angles' in currentState ? currentState.torso_angles : null
  };
}

/**
 * Get hand landmarks from the current controller state.
 * Returns landmarks for each hand in body-centric coordinates.
 */
export function getHandLandmarks(): {
  left: Record<string, vec3> | null;
  right: Record<string, vec3> | null;
} {
  const currentState = get(controllerState);

  if (!currentState || !('hand_poses' in currentState)) {
    return {
      left: null,
      right: null
    };
  }

  return {
    left: currentState.hand_poses.left?.landmarks ?? null,
    right: currentState.hand_poses.right?.landmarks ?? null
  };
}

/**
 * Clean up teleoperation device resources
 */
export function destroyTeleopDevice(): void {
  const device = get(teleopDevice);
  if (device) {
    device.close();
  }
  teleopDevice.set(null);
  controllerState.set(null);
  isTracking.set(false);
  isEngaged.set(false);
}